import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './ReactionToast.css';
import {selectReactionQueue, selectUserId} from '../../redux/reducers/privateStateReducer/selectors';
import {selectPlayers} from '../../redux/reducers/sharedStateReducer/selectors';
import {dequeueReaction, mutePlayerReactions} from '../../redux/actions';
import {getReactionTypeConfig, REACTION_RECIPIENT_ALL} from '../../configs/reactions';
import ReactionIcon from '../ReactionIcon/ReactionIcon';
import {getPlayerHexColor} from '../../utils/players/getPlayerHexColor';
import FactionSkinIcon from '../RankSkinFrame/FactionSkinIcon';
import {WAR_COLOR} from '../../styles/constants';
import ModalWindow from '../Modals/ModalWindow/ModalWindow';
import ModalButton from '../Modals/ModalWindow/ModalButton/ModalButton';
import PlayerFactionIcon from '../PlayerFactionIcon/PlayerFactionIcon';
import {REGISTRY_SINGLETON} from '../../scenes/Registry';
import {MENU_SOUND_MANAGER} from '../../scenes/MenuSoundManager';
import {getReactionSenderId} from '../../utils/reactions/getReactionSenderId';
import {usePausableTimeout} from '../../hooks/react/usePausableTimeout';
import {useMatchToastsPaused} from '../MatchToastStack/matchToastsPause';

const REACTION_TOAST_DURATION_MS = 5000;

// Звук тоста-реакции: у большинства типов реакций — тот же звук, что и у
// обычного info-тоста; у части реакций — свой уникальный звук вместо него
const playReactionSound = (reactionType, fromPlayerFactionId) => {
  const soundManager = REGISTRY_SINGLETON.getSoundManager();
  switch (reactionType) {
    case 'faction':
      MENU_SOUND_MANAGER.playFactionSelect(fromPlayerFactionId, true);
      break;
    case 'trade_offer':
      soundManager.playGuild();
      break;
    case 'culture_offer':
      soundManager.playTemple();
      break;
    case 'threat':
      soundManager.playEnemyUnit();
      break;
    default:
      soundManager.playToastInfo();
  }
};

// Тост-реакция (openspec/changes/reaction-messages): показывает первый
// элемент очереди реакций приватного стейта 5 секунд, затем удаляет его
// (DEQUEUE_REACTION) — следующий элемент очереди отображается сам.
// Клик по ВХОДЯЩЕМУ тосту — модалка мута отправителя до конца матча.
// На паузе уведомлений (на телефоне открыт список игроков или рынок) реакции
// не видно, очередь стоит, звук ждет показа
const ReactionToast = () => {
  const dispatch = useDispatch();
  const queue = useSelector(selectReactionQueue);
  const players = useSelector(selectPlayers);
  const userId = useSelector(selectUserId);

  // Кандидат на мут (playerId) — снапшот на момент клика: тост под модалкой
  // может смениться или скрыться, модалка должна пережить это
  const [muteCandidateId, setMuteCandidateId] = useState(null);

  const reaction = queue.length > 0 ? queue[0] : null;
  const reactionId = reaction ? reaction.id : null;
  const fromPlayer = reaction ? players.find((player) => player.id === reaction.fromPlayerId) : null;
  const fromPlayerFactionId = fromPlayer ? fromPlayer.factionId : null;
  const reactionType = reaction ? reaction.reactionType : null;

  const isPaused = useMatchToastsPaused();

  usePausableTimeout(
    () => dispatch(dequeueReaction()),
    REACTION_TOAST_DURATION_MS,
    {isPaused, resetKey: reactionId, enabled: reactionId !== null}
  );

  // Звук — один раз на реакцию, когда она действительно на экране
  const soundedReactionIdRef = useRef(null);
  useEffect(() => {
    if (reactionId === null || isPaused || soundedReactionIdRef.current === reactionId) {
      return;
    }
    soundedReactionIdRef.current = reactionId;
    playReactionSound(reactionType, fromPlayerFactionId);
  }, [reactionId, isPaused, reactionType, fromPlayerFactionId]);

  const muteCandidate = muteCandidateId !== null
    ? players.find((player) => player.id === muteCandidateId)
    : null;

  const handleMuteConfirm = () => {
    dispatch(mutePlayerReactions(muteCandidateId));
    setMuteCandidateId(null);
  };

  let toastContent = null;
  if (reaction && !isPaused) {
    const toPlayer = reaction.toPlayerId !== REACTION_RECIPIENT_ALL
      ? players.find((player) => player.id === reaction.toPlayerId)
      : null;
    const reactionConfig = getReactionTypeConfig(reaction.reactionType);
    // Своя — и реакция админа от имени перехваченного им бота: заглушать
    // самого себя тост не предлагает
    const isOwnReaction = reaction.fromPlayerId === getReactionSenderId(players, userId);

    if (fromPlayer && reactionConfig && (toPlayer || reaction.toPlayerId === REACTION_RECIPIENT_ALL)) {
      toastContent = (
        <ReactionToastView
          fromPlayer={fromPlayer}
          toPlayer={toPlayer}
          reactionConfig={reactionConfig}
          isOwnReaction={isOwnReaction}
          onClick={() => setMuteCandidateId(reaction.fromPlayerId)}
        />
      );
    }
  }

  return (
    <>
      {toastContent}

      {muteCandidate && (
        <ModalWindow handleClose={() => setMuteCandidateId(null)} className={'reaction-mute-modal'}>
          <div className="reaction-mute-modal-content">
            <h2>
              {'Заглушить реакции'}
              <br/>
              <FactionSkinIcon
                imgClassName={'player-info_faction-icon reaction-mute-modal-faction-icon'}
                factionId={muteCandidate.factionId}
                skinId={muteCandidate.selectedSkin}
                alt={muteCandidate.factionId}
              />
              <span style={{color: getPlayerHexColor(muteCandidate.color)}}>{muteCandidate.name}</span>
              {'?'}
            </h2>
          </div>

          <div className="reaction-mute-modal-footer">
            <ModalButton
              onClick={handleMuteConfirm}
              text={'Заглушить'}
              className={'reaction-mute-modal-button'}
            />
            <ModalButton
              onClick={() => setMuteCandidateId(null)}
              text={'Отмена'}
              className={'reaction-mute-modal-button'}
              style={{background: WAR_COLOR}}
            />
          </div>
        </ModalWindow>
      )}
    </>
  );
};

const renderPlayerBlock = (player) => (
  <div className={'reaction-toast_player'}>
    <PlayerFactionIcon player={player} skinId={player.selectedSkin}/>
    {/* Ник — светлыми чернилами плиты, цвет игрока — подчеркиванием, как
        в панели реакций: темные цвета на базальте не читались бы */}
    <span
      className={'reaction-toast_player-name'}
      style={{'--reaction-toast-player-color': getPlayerHexColor(player.color)}}
    >
      {player.name}
    </span>
  </div>
);

/**
 * Внешний вид тоста-реакции без очереди и модалки мута — его же рисует
 * витрина интерфейса матча (AdminGameUiKitPage). toPlayer пуст — реакция
 * всем; своя реакция (isOwnReaction) не кликабельна
 */
export const ReactionToastView = ({fromPlayer, toPlayer, reactionConfig, isOwnReaction, onClick}) => (
  // Обертка — под тень плиты со срезанными углами (см. Toast.js)
  <div className={'toast-shadow'}>
    <div
      className={`reaction-toast standard-background ${isOwnReaction ? '' : 'reaction-toast_clickable'}`}
      role={isOwnReaction ? undefined : 'button'}
      title={isOwnReaction ? reactionConfig.title : `${reactionConfig.title}. Нажмите, чтобы заглушить реакции игрока`}
      onClick={isOwnReaction ? undefined : onClick}
    >
      {renderPlayerBlock(fromPlayer)}

      <div className={`reaction-toast_reaction ${toPlayer ? 'reaction-toast_reaction-directed' : ''}`}>
        <ReactionIcon reaction={reactionConfig} className={'reaction-toast_reaction-icon'}/>
        {toPlayer && (
          <div className="city-capture-from-to">
            <img
              src={'/img/icons/up white.png'}
              alt={'right arrow'}
            />
          </div>
        )}
      </div>

      {toPlayer && renderPlayerBlock(toPlayer)}
    </div>
  </div>
);

export default ReactionToast;
